import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import logger from 'utils/logging/faro';

import useMxCookies from './useMxCookies';
import { fetchMediaWithAuth } from '../utils';

/**
 * Use avatar hook
 * @returns fetchAvatar - For fetching the avatar after updated or initializing the app
 * @returns changeAvatar - For changing the avatar
 * @returns clearAvatar - For clearing the avatar
 * @returns loading - The loading state when uploading or clearing the avatar
 */
const useAvatar = () => {
  const { t } = useTranslation();
  const { matrixClient, setMxAvatarUrl } = useMatrixContext();
  const { mxUserId } = useMxCookies();
  const { pushToast } = useToast();
  const [loading, setLoading] = useState(false);

  const fetchAvatar = useCallback(
    async (avatarUrl?: string) => {
      const accessToken = matrixClient.current?.getAccessToken();
      if (!avatarUrl || !accessToken) return setMxAvatarUrl('');
      const avatarDimensions = 100;
      const downloadUrl = matrixClient.current?.mxcUrlToHttp(
        avatarUrl,
        avatarDimensions,
        avatarDimensions,
        'crop',
        undefined,
        undefined,
        true
      );
      if (!downloadUrl) return;

      const response = await fetchMediaWithAuth(downloadUrl, accessToken);
      const responseData = await response.arrayBuffer();
      const blob = new Blob([responseData], { type: 'image/jpeg' });
      setMxAvatarUrl(URL.createObjectURL(blob));
    },
    [matrixClient, setMxAvatarUrl]
  );

  const changeAvatar = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setLoading(true);
      const file = target.files?.[0];
      if (!file || !matrixClient.current) return;

      const arrayBuffer = await file.arrayBuffer();
      const blob = new Blob([arrayBuffer], { type: file.type });
      const response = await matrixClient.current.uploadContent(blob);
      if (response?.content_uri) {
        await matrixClient.current.setAvatarUrl(response.content_uri);
        setMxAvatarUrl(URL.createObjectURL(blob));
      }
    } catch (error) {
      logger.error('Error uploading avatar:', { hasMatrixClient: !!matrixClient.current, error, mxUserId });
      pushToast({ title: t('error_uploading_avatar'), variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const clearAvatar = async () => {
    setLoading(true);
    await matrixClient.current?.setAvatarUrl('');
    setMxAvatarUrl('');
    setLoading(false);
  };

  return { fetchAvatar, changeAvatar, clearAvatar, loading };
};

export default useAvatar;
